import { colors, COLORS } from '@constants/colors';

const CONTROL_HEIGHT = 36;

export const useBrandingBarStyles = () => ({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px',
    height: '46px',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    width: 'fit-content',
    maxWidth: 'calc(100vw - 48px)',
  },
  brandButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: CONTROL_HEIGHT,
    cursor: 'pointer',
    padding: '0 6px 0 5px',
    borderRadius: '9px',
    color: COLORS.WHITE,
    flexShrink: 1,
    minWidth: 0,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  brandSwatchRow: {
    display: 'flex',
    width: 32,
    height: 20,
    borderRadius: '5px',
    overflow: 'hidden',
    gap: '1px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    flexShrink: 0,
  },
  brandSwatchStripe: {
    flex: 1,
    height: '100%',
  },
  brandSwatchPlaceholder: {
    flex: 1,
    height: '100%',
    background:
      'repeating-linear-gradient(45deg, rgba(58,58,70,1) 0px, rgba(58,58,70,1) 5px, rgba(46,46,58,1) 5px, rgba(46,46,58,1) 10px)',
  },
  brandName: {
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 600,
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  divider: {
    width: '1px',
    height: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    flexShrink: 0,
  },
  autosaveRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '5px',
    color: colors.white_50,
    fontSize: '11px',
    whiteSpace: 'nowrap',
    padding: '0 4px',
    flexShrink: 0,
    width: '124px',
  },
  autosaveDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },

  activateButton: {
    color: COLORS.WHITE,
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    textTransform: 'none',
    height: CONTROL_HEIGHT,
    padding: '0 14px',
    borderRadius: '9px',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minWidth: 'unset',
    flexShrink: 0,
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(69, 84, 229, 0.35)',
      color: 'rgba(255, 255, 255, 0.55)',
    },
  },
  activeLabel: {
    color: COLORS.GREEN,
    fontSize: '12px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
    padding: '0 6px',
    flexShrink: 0,
  },
  activeLabelDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: COLORS.GREEN,
    boxShadow: '0 0 6px rgba(29, 171, 86, 0.9)',
  },
  activeLabelDotInactive: {
    backgroundColor: colors.white_40,
    boxShadow: 'none',
  },
  exitButton: {
    color: colors.white_60,
    width: 44,
    height: CONTROL_HEIGHT,
    borderRadius: '9px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    flexShrink: 0,
    '&:hover': {
      color: COLORS.WHITE,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },

  /* QuickSwitcher */
  quickSwitcherPaper: {
    backgroundColor: 'rgba(20, 20, 24, 0.97)',
    backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    width: 320,
    overflow: 'hidden',
  },
  quickSearchWrap: {
    padding: '10px 12px 8px',
  },
  quickSearchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      borderRadius: '8px',
      color: COLORS.WHITE,
      fontSize: '13px',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.18)' },
      '&.Mui-focused fieldset': { borderColor: COLORS.MYTAVERSE_BLUE },
    },
    '& .MuiOutlinedInput-input': { padding: '7px 6px' },
  },
  quickList: {
    maxHeight: '300px',
    overflowY: 'auto',
    padding: '4px',
  },
  quickRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 8px',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  quickRowActive: {
    backgroundColor: 'rgba(69, 84, 229, 0.18)',
    '&:hover': { backgroundColor: 'rgba(69, 84, 229, 0.24)' },
  },
  quickName: {
    flex: 1,
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  quickLiveDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: COLORS.GREEN,
    flexShrink: 0,
  },
  /* Status slot keeps the right column the same width whether the row shows
     the static "currently live" indicator or the clickable activate button,
     so all icons vertically align across rows. */
  quickStatusSlot: {
    width: 30,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: 'default',
  },
  quickStatusButton: {
    width: 30,
    height: 30,
    padding: 0,
    color: colors.white_50,
    flexShrink: 0,
    '&:hover': {
      color: COLORS.GREEN,
      backgroundColor: 'rgba(29, 171, 86, 0.12)',
    },
  },
  quickStatusLiveIcon: {
    fontSize: 18,
    color: COLORS.GREEN,
  },
  quickStatusInactiveIcon: {
    fontSize: 18,
    color: 'inherit',
  },
  quickPowerButton: {
    // kept for backwards-compat with anything that may still reference it
    color: colors.white_50,
    padding: '4px',
  },
  quickFooter: {
    display: 'flex',
    gap: '8px',
    padding: '8px 10px 10px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  },
  quickFooterButton: {
    flex: 1,
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.16)',
    textTransform: 'none',
    padding: '6px 10px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
  quickEmpty: {
    color: colors.white_40,
    fontSize: '12px',
    padding: '14px 12px',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  quickLoader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '14px 12px',
  },
});
